import { useElementBounding } from '@vueuse/core';
import round from 'lodash/round';
import { defineComponent, ref, useSlots, computed } from 'vue-demi';
import './TimeFormat-a7f5565b.js';
import './dom-53c9635b.js';
import { u as useResponsive } from './useResponsive-491da8c6.js';
import '@onereach/styles/tailwind.config.json';
import { openBlock, createElementBlock, normalizeClass, Fragment, renderSlot, createElementVNode, createTextVNode, toDisplayString, createCommentVNode } from 'vue';

var ProgressColor;
(function (ProgressColor) {
  ProgressColor["Auto"] = "auto";
  ProgressColor["Primary"] = "primary";
  ProgressColor["Success"] = "success";
  ProgressColor["Warning"] = "warning";
  ProgressColor["Error"] = "error";
})(ProgressColor || (ProgressColor = {}));
var ProgressType;
(function (ProgressType) {
  ProgressType["Linear"] = "linear";
  ProgressType["Circle"] = "circle";
})(ProgressType || (ProgressType = {}));

const ProgressRoot = [
// Typography
'typography-caption-regular',
// Theme
'text-outline', 'dark:text-outline-dark'];
const ProgressRootTypes = {
  [ProgressType.Linear]: [
  // Layout
  'flex', 'flex-col',
  // Box
  'w-full', 'min-w-[160px]', 'max-w-[295px]'],
  [ProgressType.Circle]: ['-rotate-90']
};
const ProgressHeader = [
// Layout
'flex', 'flex-row',
// Spacing
'mb-[6px]'];
const ProgressHeaderLabel = [
// Typography
'typography-caption-bold',
// Theme
'text-on-background', 'dark:text-on-background-dark'];
const ProgressHeaderPercent = [
// Spacing
'ml-auto'];
const ProgressBarBackground = [
// Theme
'stroke-surface-variant', 'dark:stroke-surface-variant-dark'];
const ProgressBarMain = [
// Position
'origin-center',
// Animation
'transition-[stroke-dashoffset]'];
const ProgressBarAnimationTypes = {
  [ProgressType.Linear]: [
  // Animation
  'animate-linear-loader'],
  [ProgressType.Circle]: [
  // Animation
  'animate-circular-loader']
};
const ProgressBarColors = {
  [ProgressColor.Primary]: [
  // Theme
  'stroke-primary-hover', 'dark:stroke-primary-hover-dark'],
  [ProgressColor.Success]: [
  // Theme
  'stroke-success-hover', 'dark:stroke-success-hover-dark'],
  [ProgressColor.Warning]: [
  // Theme
  'stroke-warning-hover', 'dark:stroke-warning-hover-dark'],
  [ProgressColor.Error]: [
  // Theme
  'stroke-error-hover', 'dark:stroke-error-hover-dark']
};
const ProgressFooter = [
// Spacing
'mt-[2px]'];

var script = defineComponent({
  name: 'OrProgress',
  props: {
    maxValue: {
      type: Number,
      required: false,
      default: 1
    },
    value: {
      type: Number,
      required: false,
      default: null
    },
    progress: {
      type: Number,
      required: false,
      default: null
    },
    precision: {
      type: Number,
      required: false,
      default: 2
    },
    type: {
      type: String,
      required: false,
      default: () => ProgressType.Linear
    },
    color: {
      type: String,
      required: false,
      default: () => ProgressColor.Auto
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  expose: ['root'],
  setup(props) {
    // Refs
    const root = ref();
    // Slots
    const {
      header: headerSlot,
      label: labelSlot,
      percent: percentSlot,
      footer: footerSlot
    } = useSlots();
    // State
    const {
      width
    } = useElementBounding(root);
    const localProgress = computed(() => {
      var _a, _b, _c;
      return (_a = props.progress) !== null && _a !== void 0 ? _a : ((_b = props.value) !== null && _b !== void 0 ? _b : 0) / ((_c = props.maxValue) !== null && _c !== void 0 ? _c : 1);
    });
    const localValue = computed(() => {
      var _a, _b, _c;
      return (_a = props.value) !== null && _a !== void 0 ? _a : ((_b = props.progress) !== null && _b !== void 0 ? _b : 0) * ((_c = props.maxValue) !== null && _c !== void 0 ? _c : 1);
    });
    const percent = computed(() => round(localProgress.value * 100, props.precision) + '%');
    const isLinearType = computed(() => props.type === ProgressType.Linear);
    const isCircleType = computed(() => props.type === ProgressType.Circle);
    const isShowingHeader = computed(() => !!headerSlot || !!labelSlot || !!percentSlot);
    const isShowingFooter = computed(() => !!footerSlot);
    const localColor = computed(() => {
      if (props.loading) return ProgressColor.Primary;
      if (props.color === ProgressColor.Auto) {
        if (localProgress.value <= 0.5) {
          return ProgressColor.Success;
        } else if (localProgress.value <= 0.95) {
          return ProgressColor.Warning;
        } else {
          return ProgressColor.Error;
        }
      } else {
        return Object.values(ProgressColor).includes(props.color) ? props.color : ProgressColor.Success;
      }
    });
    const computedSize = computed(() => isMobile.value ? 20 : 16);
    // Styles
    const rootStyles = computed(() => ['or-progress-v3', ...ProgressRoot, ...ProgressRootTypes[props.type]]);
    const headerStyles = computed(() => [...ProgressHeader]);
    const labelStyles = computed(() => [...ProgressHeaderLabel]);
    const percentStyles = computed(() => [...ProgressHeaderPercent]);
    const footerStyles = computed(() => [...ProgressFooter]);
    const progressBarBackgroundStyles = computed(() => [...ProgressBarBackground]);
    const progressBarMainPartStyles = computed(() => [...ProgressBarMain, ...(props.loading ? ProgressBarAnimationTypes[props.type] : []), ...ProgressBarColors[localColor.value]]);
    const strokeDasharray = computed(() => isLinearType.value ? width.value - 4 :
    // 2 + 2 px horizontal padding for rounding
    isCircleType.value ? 14 * Math.PI :
    // 14 = diameter = viewBox - stroke-width
    0);
    const strokeWidth = computed(() => {
      return width.value ? width.value - 2 : '100%';
    });
    const strokeDashoffset = computed(() => props.loading ? undefined : (1 - localProgress.value) * strokeDasharray.value);
    // Helpers
    const {
      isMobile
    } = useResponsive();
    return {
      root,
      rootStyles,
      headerStyles,
      labelStyles,
      percentStyles,
      footerStyles,
      progressBarBackgroundStyles,
      progressBarMainPartStyles,
      strokeDasharray,
      strokeDashoffset,
      strokeWidth,
      localProgress,
      localValue,
      percent,
      isLinearType,
      isCircleType,
      isShowingHeader,
      isShowingFooter,
      localColor,
      computedSize
    };
  }
});

const _hoisted_1 = {
  width: "100%",
  height: "2"
};
const _hoisted_2 = {
  fill: "none",
  "stroke-width": "2",
  "stroke-linecap": "round"
};
const _hoisted_3 = ["x2"];
const _hoisted_4 = ["x2", "stroke-dasharray", "stroke-dashoffset"];
const _hoisted_5 = ["width", "height"];
const _hoisted_6 = {
  fill: "none",
  "stroke-width": "2",
  "stroke-linecap": "round"
};
const _hoisted_7 = ["stroke-dasharray", "stroke-dashoffset"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles)
  }, [_ctx.isLinearType ? (openBlock(), createElementBlock(Fragment, {
    key: 0
  }, [_ctx.isShowingHeader ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass(_ctx.headerStyles)
  }, [renderSlot(_ctx.$slots, "header", {}, () => [createElementVNode("div", {
    class: normalizeClass(_ctx.labelStyles)
  }, [renderSlot(_ctx.$slots, "label")], 2 /* CLASS */), createElementVNode("div", {
    class: normalizeClass(_ctx.percentStyles)
  }, [renderSlot(_ctx.$slots, "percent", {
    value: _ctx.percent
  }, () => [createTextVNode(toDisplayString(_ctx.percent), 1 /* TEXT */)])], 2 /* CLASS */)])], 2 /* CLASS */)) : createCommentVNode("v-if", true), (openBlock(), createElementBlock("svg", _hoisted_1, [createElementVNode("g", _hoisted_2, [createElementVNode("line", {
    class: normalizeClass(_ctx.progressBarBackgroundStyles),
    x1: "2",
    y1: "50%",
    x2: _ctx.strokeWidth,
    y2: "50%"
  }, null, 10 /* CLASS, PROPS */, _hoisted_3), createElementVNode("line", {
    class: normalizeClass(_ctx.progressBarMainPartStyles),
    x1: "2",
    y1: "50%",
    x2: _ctx.strokeWidth,
    y2: "50%",
    "stroke-dasharray": _ctx.strokeDasharray,
    "stroke-dashoffset": _ctx.strokeDashoffset
  }, null, 10 /* CLASS, PROPS */, _hoisted_4)])])), _ctx.isShowingFooter ? (openBlock(), createElementBlock("div", {
    key: 1,
    class: normalizeClass(_ctx.footerStyles)
  }, [renderSlot(_ctx.$slots, "footer", {
    value: _ctx.localValue
  })], 2 /* CLASS */)) : createCommentVNode("v-if", true)], 64 /* STABLE_FRAGMENT */)) : _ctx.isCircleType ? (openBlock(), createElementBlock("svg", {
    key: 1,
    viewBox: "0 0 16 16",
    width: _ctx.computedSize,
    height: _ctx.computedSize
  }, [createElementVNode("g", _hoisted_6, [createElementVNode("circle", {
    class: normalizeClass(_ctx.progressBarBackgroundStyles),
    cx: "50%",
    cy: "50%",
    r: "7"
  }, null, 2 /* CLASS */), createElementVNode("circle", {
    class: normalizeClass(_ctx.progressBarMainPartStyles),
    cx: "50%",
    cy: "50%",
    r: "7",
    "stroke-dasharray": _ctx.strokeDasharray,
    "stroke-dashoffset": _ctx.strokeDashoffset
  }, null, 10 /* CLASS, PROPS */, _hoisted_7)])], 8 /* PROPS */, _hoisted_5)) : createCommentVNode("v-if", true)], 2 /* CLASS */);
}

script.render = render;
script.__file = "src/components/or-progress-v3/OrProgress.vue";

export { ProgressColor as P, ProgressType as a, script as s };
